import TemplateListFilters from "./components/TemplateListFilters";
import React from "react";
import { Outlet, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { 
  Library, 
  ListChecks, 
  FileBarChart,
  Clock,
  RefreshCw,
  Plus
} from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import WorkspaceTitleHeader from "../../components/common/WorkspaceTitleHeader";

const tabs = [
  { label: "Approved", to: "/app/template-builder/approved", icon: <FileBarChart size={16} />, key: "approved", codes: [FK.TEMPLATE_BUILDER_APPROVED_TEMPLATES_VIEW] },
  { label: "Library", to: "/app/template-builder/library", icon: <Library size={16} />, key: "library", codes: [FK.TEMPLATE_BUILDER_LIBRARY_BROWSE] },
  { label: "Drafts", to: "/app/template-builder/drafts", icon: <ListChecks size={16} />, key: "drafts", codes: [FK.TEMPLATE_BUILDER_CREATE_DRAFT, FK.TEMPLATE_BUILDER_DRAFT_EDITOR_VIEW] },
  { label: "Pending", to: "/app/template-builder/pending", icon: <Clock size={16} />, key: "pending", codes: [FK.TEMPLATE_BUILDER_PENDING_TEMPLATES_VIEW] },
  { label: "Rejected", to: "/app/template-builder/rejected", icon: <FileBarChart size={16} />, key: "rejected", codes: [FK.TEMPLATE_BUILDER_APPROVED_TEMPLATES_VIEW] },
];

export default function TemplateBuilderLayout() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { effectiveBusinessId, businessId: ctxBusinessId, can, hasAllAccess } = useAuth();

  const businessId = React.useMemo(
    () =>
      effectiveBusinessId ||
      ctxBusinessId ||
      localStorage.getItem("businessId") ||
      null,
    [ctxBusinessId, effectiveBusinessId]
  );

  const [summary, setSummary] = React.useState({ approved: 0, pending: 0, rejected: 0, drafts: 0, library: 0 });
  const [syncing, setSyncing] = React.useState(false);
  const summaryRequestRef = React.useRef(0);

  const loadSummary = React.useCallback(async () => {
    const request = ++summaryRequestRef.current;
    if (!businessId) return;
    try {
      const res = await axiosClient.get(`templates/summary/${businessId}`, { __silentToast: true });
      if (request === summaryRequestRef.current && res?.data?.success) {
        setSummary(res.data);
      }
    } catch (e) {
      // silent
    }
  }, [businessId]);

  const invalidateSummary = React.useCallback(() => { summaryRequestRef.current++; }, []);
  React.useEffect(() => {
    setSummary({ approved: 0, pending: 0, rejected: 0, drafts: 0, library: 0 });
    loadSummary();
    return invalidateSummary;
  }, [loadSummary, invalidateSummary]);

  const handleSync = async () => {
    if (!businessId || syncing) return;
    setSyncing(true);
    try {
      const { data } = await axiosClient.post(`templates/sync/${businessId}`);
      if (data.success) {
        toast.success(data.message || "Sync started...");
        loadSummary();
        // Trigger refresh in children by updating search params
        const next = new URLSearchParams(searchParams);
        next.set("v", Date.now().toString());
        navigate({ pathname, search: next.toString(), hash }, { replace: true });
      } else {
        toast.error(data.message || "Sync failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Sync error");
    } finally {
      setSyncing(false);
    }
  };

  // Helper to check active state
  const isActive = (path) => pathname === path || pathname.startsWith(`${path}/`);

  return (
    <div className="min-h-screen min-w-0 w-full bg-[#f8f9fa] flex flex-col">
       {/* Top Header Bar */}
       <header className="bg-white border-b border-slate-200 px-6 py-3">
         <div className="max-w-7xl mx-auto w-full min-w-0 flex flex-col gap-3">

            
            <WorkspaceTitleHeader
              icon={FileBarChart}
              title="Template Builder"
              subtitle="Create, validate, manage, and submit your WhatsApp templates."
            />

            {/* Navigation Tabs & Search/Filter Row */}
            <div role="toolbar" aria-label="Template navigation and filters" className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 mt-2 border-b border-transparent">
              <div className="flex items-center gap-0 min-w-0 flex-1 overflow-x-auto">
                {tabs.filter(tab => hasAllAccess || tab.codes.some(code => can?.(code))).map((tab) => {
                  const active = isActive(tab.to);
                  return (
                    <Link
                      key={tab.to}
                      to={{ pathname: tab.to, search: searchParams.toString(), hash }}
                      aria-current={active ? "page" : undefined}
                      className={`
                        relative shrink-0 whitespace-nowrap px-2 py-2 text-sm font-medium rounded-t-lg flex items-center gap-1 transition-all
                        ${active 
                          ? "text-emerald-700 bg-white border-b-2 border-emerald-600 shadow-sm z-10" 
                          : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        }
                      `}
                    >
                      {tab.icon}
                      {tab.label}
                      {summary[tab.key] > 0 && (
                        <span className={`
                          ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full
                          ${active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"}
                        `}>
                          {summary[tab.key]}
                        </span>
                      )}
                      {active && (
                        <div className="absolute -bottom-[1px] left-0 right-0 h-[1px] bg-white" />
                      )}
                    </Link>
                  );
                })}
              </div>

              <div className="w-full lg:w-96 shrink-0"><TemplateListFilters /></div>

              <div className="flex shrink-0 flex-wrap lg:flex-nowrap items-center gap-2 mb-1 whitespace-nowrap">
                {(hasAllAccess || can?.(FK.TEMPLATE_BUILDER_CREATE_DRAFT)) && <button
                  type="button"
                  onClick={() => navigate("/app/template-builder/drafts?create=1")}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-all shadow-sm"
                  title="Create a new template from scratch"
                >
                  <Plus size={14} />
                  New
                </button>}
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={!businessId || syncing}
                  aria-busy={syncing}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 transition-all shadow-sm"
                  title="Sync templates from Meta"
                >
                  <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                  Sync
                </button>
              </div>
            </div>
          </div>
        </header>

       {/* Main Content Area */}
       <main className="flex-1 max-w-7xl mx-auto w-full p-4">
         <Outlet />
       </main>
    </div>
  );
}

